import React, { useMemo } from 'react';
import { UNIT_MAP, getTopLevelChapter, getLineTotal } from '../../utils/boqHelpers';
import QuoteDetailView from '../Vendors/QuoteDetailView';

/**
 * BOQQuoteDetail — Shows a single vendor's priced BOQ table.
 * Thin wrapper around QuoteDetailView that shapes BOQ-specific data
 * (mergedRows, bbChapters, quoteItems) into the generic groups/totals shape.
 */
const BOQQuoteDetail = ({
  quote,
  quoteItems,
  mergedRows,
  vendorMap,
  bbChapters,
  costTypes,
  onBack,
  onShare,
}) => {
  const vendor = vendorMap.get(quote.master_vendor_id);

  // Build chapter lookup
  const chapterById = useMemo(() => {
    const map = new Map();
    bbChapters?.forEach(ch => map.set(ch.id, ch));
    return map;
  }, [bbChapters]);

  // Build a map: project_item_id → quote_item (for looking up vendor pricing per BOQ row)
  const priceByItemId = useMemo(() => {
    const map = new Map();
    quoteItems?.forEach(qi => {
      qi.project_item_ids?.forEach(pid => map.set(pid, qi));
    });
    return map;
  }, [quoteItems]);

  // Build groups shaped for QuoteDetailView — one per top-level BB chapter
  const groups = useMemo(() => {
    if (!mergedRows?.length) return [];
    const NO_CHAPTER_KEY = '__no_chapter__';
    const groupMap = new Map();

    mergedRows.forEach(item => {
      const itemChapterId =
        item.master_items?.bb_chapter_id || item.bb_chapter_id || NO_CHAPTER_KEY;
      const topChapter = getTopLevelChapter(itemChapterId, chapterById);
      const groupKey = topChapter?.id || NO_CHAPTER_KEY;

      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, {
          chapterId: groupKey,
          chapterCode: topChapter?.code || '',
          chapterName: topChapter?.name_he || 'ללא פרק',
          items: [],
        });
      }
      groupMap.get(groupKey).items.push(item);
    });

    // Sort items within groups by sub-chapter code
    groupMap.forEach(group => {
      group.items.sort((a, b) => {
        const aId = a.master_items?.bb_chapter_id || a.bb_chapter_id;
        const bId = b.master_items?.bb_chapter_id || b.bb_chapter_id;
        const aCode = aId ? chapterById.get(aId)?.code || '' : '';
        const bCode = bId ? chapterById.get(bId)?.code || '' : '';
        return aCode.localeCompare(bCode, undefined, { numeric: true });
      });
    });

    const sortedGroups = Array.from(groupMap.values()).sort((a, b) => {
      if (a.chapterId === '__no_chapter__') return 1;
      if (b.chapterId === '__no_chapter__') return -1;
      return a.chapterCode.localeCompare(b.chapterCode, undefined, { numeric: true });
    });

    // Shape groups + items for QuoteDetailView
    return sortedGroups.map(g => {
      const shapedItems = g.items.map(item => {
        const itemName = item.name_override || item.master_items?.item_name || '—';
        const costTypeId = item.master_items?.cost_type_id || item.details?.cost_type_id;
        const costType = costTypes?.find(ct => ct.id === costTypeId);
        const unitLabel = costType ? UNIT_MAP[costType.name] || costType.name : '—';

        // Find vendor pricing for this item
        const sourceIds = item._sourceIds || [item.id];
        const qi = sourceIds.reduce((found, id) => found || priceByItemId.get(id), null);
        const isSkipped = qi && qi.line_price == null;
        const qty = item.quantity || 1;
        const dd = qi?.display_data || {};
        const costTypeName = dd.cost_type_name || costType?.name || '';
        const linePrice = qi?.line_price != null ? Number(qi.line_price) : null;
        const lineTotal = linePrice != null ? getLineTotal(linePrice, qty, costTypeName) : null;

        return {
          id: item.id,
          name: itemName,
          unitLabel,
          quantity: qty,
          linePrice,
          lineTotal,
          lineNotes: qi?.line_notes || null,
          isSkipped,
        };
      });

      // Sum of line_price for this group (matches previous groupVendorTotal behavior)
      const groupTotal = g.items.reduce((sum, item) => {
        const sourceIds = item._sourceIds || [item.id];
        const qi = sourceIds.reduce((found, id) => found || priceByItemId.get(id), null);
        return sum + (qi?.line_price ? Number(qi.line_price) : 0);
      }, 0);

      const headerLabel = g.chapterCode ? `${g.chapterCode} - ${g.chapterName}` : g.chapterName;

      return {
        id: g.chapterId,
        headerLabel,
        groupTotal,
        items: shapedItems,
      };
    });
  }, [mergedRows, chapterById, priceByItemId, costTypes]);

  // Calculate totals — uses getLineTotal to account for per-unit cost types (price × qty)
  const totals = useMemo(() => {
    let sum = 0;
    let pricedCount = 0;
    let skippedCount = 0;
    quoteItems?.forEach(qi => {
      if (qi.line_price != null) {
        const dd = qi.display_data || {};
        sum += getLineTotal(Number(qi.line_price), dd.quantity || 1, dd.cost_type_name || '');
        pricedCount++;
      } else {
        skippedCount++;
      }
    });
    return { sum, pricedCount, skippedCount, totalItems: (quoteItems || []).length };
  }, [quoteItems]);

  return (
    <QuoteDetailView
      vendorName={vendor?.name}
      vendorContact={vendor?.contact_person}
      groups={groups}
      totals={totals}
      onBack={onBack}
      onShare={onShare}
      showUnitAndQty={true}
    />
  );
};

export default BOQQuoteDetail;
